/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { useSettings } from '@/data/Settings';
import { STORE_LOCATOR_STATE_KEY } from '@/data/constants/storeLocator';
import { useStoreLocatorBaseState } from '@/data/state/byStore/storeLocator';
import { getStateUpdater, useSetState, useTrackedState } from '@/data/state/provider';
import { SelectedStoreLocator, StoreDetails } from '@/data/types/Store';
import { getDefaultStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { getStateKey } from '@/data/utils/getStateKey';
import { useCallback, useMemo } from 'react';

/**
 * React hook for use by the presentation layer to read selected store state
 * data and expose event handlers (actions) related to data changes.
 */
const STORE_LOCATOR_BASE = {
	selectedStore: {} as StoreDetails,
};

export const useStoreLocatorState = () => {
	const { settings } = useSettings();
	const key = useMemo(() => getStateKey(STORE_LOCATOR_STATE_KEY, settings), [settings]);
	const baseStateLocalStorage = useStoreLocatorBaseState(key);
	const keyCurrentStorageReact = 'HCS-HDM-B2C-Store-currentStore';
	const currentStoreReact =
		typeof window !== 'undefined' ? window?.localStorage?.getItem(keyCurrentStorageReact) : null;
	const baseState = !currentStoreReact
		? baseStateLocalStorage
		: baseStateLocalStorage.selectedStore.id !== currentStoreReact
		? STORE_LOCATOR_BASE
		: baseStateLocalStorage;
	const storeLocatorUpdater = useMemo(() => getStateUpdater({ key, baseState }), [baseState, key]);

	const setState = useSetState();
	const fullState = useTrackedState();
	const storeLocator = fullState[key] as SelectedStoreLocator;

	const selectStore = useCallback(
		(storeDetails: StoreDetails) => {
			typeof window !== 'undefined'
				? window?.localStorage?.setItem(keyCurrentStorageReact, storeDetails.id)
				: null;
			return storeLocatorUpdater({
				setState,
				now: (storeLocator) => ({ ...storeLocator, selectedStore: storeDetails }),
			});
		},
		[setState, storeLocatorUpdater]
	);

	const resetSelectedStore = useCallback(
		() =>
			storeLocatorUpdater({
				setState,
				later: async (storeLocator) => ({
					...storeLocator,
					selectedStore: {} as StoreDetails,
				}),
			}),
		[setState, storeLocatorUpdater]
	);
	const defaultStore = !!baseState?.selectedStore?.id
		? baseState
		: { ...baseState, selectedStore: getDefaultStoreDataByPhysicalStoreId() };

	return {
		storeLocator: storeLocator || defaultStore,
		actions: { selectStore, resetSelectedStore },
	};
};
